import React, { useState, useEffect, useRef } from "react";
import { fetchChatMessages } from "../services/chatService";
import { Icon } from "@iconify/react";
import "../../css/chat.css";
import moment from "moment";


// Who deleted a message, in words (2026-09-26). The guard deletes as
// app_system. A member's phone deletes as the sender both when the member
// long-presses Delete and when the app's own content check pulls a message
// it blocked, and CometChat records the two the same way, so the label
// cannot say "by the sender".
const DELETED_LABELS = {
    curiouzz: "Removed by Curiouzz",
    sender: "Deleted from the sender's phone",
};
const SENDER_DELETE_HINT =
    "The member may have deleted this, or the app's content check pulled it. CometChat records both the same way.";

const roleOf = (msg) => {
    if (msg?.deletedByRole) return msg.deletedByRole;
    if (msg?.deletedBy === "app_system") return "curiouzz";
    if (msg?.deletedBy && String(msg.deletedBy) === String(msg.sender)) return "sender";
    return "unknown";
};

// A "deleted" event row says nothing the deleted message's own label does
// not already say, so it is folded into that message when both are here.
const deletedTargetOf = (msg) =>
    msg?.category === "action" && msg?.data?.action === "deleted"
        ? String(msg?.data?.entities?.on?.entity?.id ?? "")
        : null;

// A chat id the page could actually fill in. A page whose record had no
// member id sends "cm_undefined"; that is no conversation at all.
const isChatId = (v) => !!v && !/^(cm_)?(undefined|null|nan)?$/i.test(String(v));

const ChatMessagesTable = ({ defaultUid, defaultOtherUser }) => {
    const [uid] = useState(defaultUid);
    const [otherUser] = useState(defaultOtherUser);
    // Opened without two members (/chat reached directly, for one): say so,
    // and never ask the server (2026-09-26).
    const hasPair = isChatId(uid) && isChatId(otherUser);

    const [messages, setMessages] = useState([]);
    // Loading from the first paint, so an unread conversation never shows
    // "No messages in this conversation." for a moment.
    const [loading, setLoading] = useState(hasPair);
    // Why the conversation could not be loaded (2026-09-26). A failed
    // request used to leave the list empty and read "No messages found".
    const [loadError, setLoadError] = useState("");
    const [info, setInfo] = useState({ departed: [], members: {}, truncated: false });
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    // 0 = the whole conversation. The server sends every message of the
    // pair at once, so paging happens here and the page buttons are real.
    const [limit, setLimit] = useState(0);

    const chatBoxRef = useRef(null);

    /* ---------------- SENDER NAME RESOLVER ---------------- */
    const getSenderName = (msg) => {
        if (!msg) return "Unknown";

        const senderId = String(msg.sender);

        const sender =
            msg?.data?.entities?.sender?.entity ||
            msg?.data?.entities?.by?.entity ||
            msg?.data?.entities?.on?.entity?.data?.entities?.sender?.entity;

        const receiver =
            msg?.data?.entities?.receiver?.entity ||
            msg?.data?.entities?.for?.entity ||
            msg?.data?.entities?.on?.entity?.data?.entities?.receiver?.entity;

        if (sender?.uid === senderId) return sender.name;
        if (receiver?.uid === senderId) return receiver.name;

        return info.members?.[senderId] || "Unknown";
    };

    /* ---------------- MESSAGE TEXT BUILDER ---------------- */
    // What a message that is not plain text is (2026-09-24). The viewer used
    // to print "-" for all of these, which read as missing text.
    const labelOf = (msg) => {
        if (msg?.deletedAt) {
            const by = DELETED_LABELS[roleOf(msg)] || "Deleted";
            const text = msg?.removedText || msg?.data?.text;
            return text ? `${by}: ${text}` : by;
        }
        if (msg?.category === "action") {
            const action = msg?.data?.action;
            if (action === "deleted") return "A message was deleted";
            if (action === "edited") return "A message was edited";
            return "Chat event";
        }
        if (msg?.category === "custom" && msg?.type === "curiouzz_notice") {
            return `Curiouzz: ${msg?.data?.customData?.text || "a message was removed"}`;
        }
        if (msg?.category === "custom" && msg?.type === "meetup_suggestion") {
            const name = msg?.data?.customData?.name;
            return name ? `Meetup suggestion: ${name}` : "Meetup suggestion";
        }
        return null;
    };

    const getMessageText = (msg) => {
        const label = labelOf(msg);
        if (label) return label;
        if (msg.category === "call") {
            const callType = msg.type === "video" ? "Video Call" : "Audio Call";
            const action = msg?.data?.action || "started";
            return `${callType} ${action}`;
        }

        if (msg?.data?.metadata?.isGift) {
            const gift = msg.data.metadata;
            return `${gift.giftEmoji || "🎁"} ${gift.giftName}\n${gift.giftDescription}`;
        }

        return msg?.data?.text || "-";
    };

    const MediaWithLoader = ({ type, src }) => {
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(false);

        if (error) {
            return (
                <div className="text-danger small">
                    Failed to load {type}
                </div>
            );
        }

        return (
            <div style={{ position: "relative", maxWidth: "220px" }}>
                {loading && (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f5f5f5",
                            borderRadius: "8px",
                            zIndex: 1,
                        }}
                    >
                        <span className="spinner-border spinner-border-sm text-primary" />
                    </div>
                )}

                {type === "image" ? (
                    <img
                        src={src}
                        alt="chat-media"
                        style={{
                            maxWidth: "200px",
                            borderRadius: "8px",
                            display: loading ? "none" : "block",
                        }}
                        onLoad={() => setLoading(false)}
                        onError={() => {
                            setLoading(false);
                            setError(true);
                        }}
                    />
                ) : (
                    <video
                        controls
                        src={src}
                        style={{
                            maxWidth: "200px",
                            borderRadius: "8px",
                            display: loading ? "none" : "block",
                        }}
                        onLoadedData={() => setLoading(false)}
                        onError={() => {
                            setLoading(false);
                            setError(true);
                        }}
                    />
                )}
            </div>
        );
    };

    const renderMessageContent = (msg) => {
        const label = labelOf(msg);
        if (label) {
            const hint = msg?.deletedAt && roleOf(msg) === "sender" ? SENDER_DELETE_HINT : undefined;
            return <span className="fst-italic" title={hint}>{label}</span>;
        }
        if (msg.category === "call") {
            const callType = msg.type === "video" ? "Video Call" : "Audio Call";
            const action = msg?.data?.action || "started";
            return <span>{`${callType} ${action}`}</span>;
        }

        if (msg?.data?.metadata?.isGift) {
            const gift = msg.data.metadata;
            return (
                <span>
                    {gift.giftEmoji || "🎁"} {gift.giftName}
                    <br />
                    {gift.giftDescription}
                </span>
            );
        }

        if (msg.type === "image" && msg?.data?.url) {
            return <MediaWithLoader type="image" src={msg.data.url} />;
        }

        if (msg.type === "video" && msg?.data?.url) {
            return <MediaWithLoader type="video" src={msg.data.url} />;
        }

        return msg?.data?.text || "-";
    };


    /* ---------------- MESSAGE TYPE ---------------- */
    const getMessageClass = (msg, isMine) => {
        if (msg.category === "call") return "chat-system-message";
        if (msg.category === "action") return "chat-system-message";
        if (msg.category === "custom" && msg.type === "curiouzz_notice") return "chat-system-message";
        if (msg?.data?.metadata?.isGift) return "chat-system-message";
        return isMine ? "chat-text-sent" : "chat-text-received";
    };

    /* ---------------- LOAD MESSAGES ---------------- */
    // One request per conversation: the server returns the whole thread,
    // so search and paging below never go back to it.
    const loadMessages = async () => {
        setLoading(true);
        setLoadError("");
        try {
            const res = await fetchChatMessages({ uid, otherUser });
            if (!res || res.success === false || !Array.isArray(res.data)) {
                setMessages([]);
                setLoadError(res?.message || "The server's answer could not be read.");
                return;
            }
            setMessages(res.data);
            setInfo({
                departed: Array.isArray(res.departed) ? res.departed : [],
                members: res.members || {},
                truncated: !!res.truncated,
            });
        } catch (error) {
            console.error("Failed to fetch messages", error);
            const status = error?.response?.status;
            const reason = error?.response?.data?.message || error?.message || "Unknown error.";
            setMessages([]);
            setLoadError(status ? `${reason} (HTTP ${status})` : reason);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (hasPair) {
            loadMessages();
        }
    }, [uid, otherUser, hasPair]);

    const shownIds = new Set(messages.map((m) => String(m.id)));
    const needle = search.trim().toLowerCase();
    // The rows the page shows: every message, with each delete event folded
    // into the message it deleted.
    const rows = messages.filter((m) => {
        const target = deletedTargetOf(m);
        return !(target && shownIds.has(target));
    });
    const visible = rows.filter((m) => !needle || String(getMessageText(m)).toLowerCase().includes(needle));

    const total = visible.length;
    const totalPages = limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;
    const pageRows = limit > 0 ? visible.slice((page - 1) * limit, page * limit) : visible;

    /* ---------------- AUTO SCROLL ---------------- */
    useEffect(() => {
        if (chatBoxRef.current) {
            chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
    }, [messages, page, limit, search]);

    // A member whose account was deleted (2026-09-26). The conversation is
    // still read in full from the chat record; this says why their profile
    // is gone. It never says who deleted it: an admin deleting an account
    // from the portal sets the same date as a member deleting their own.
    const departedNote = () => {
        const gone = info.departed || [];
        if (!gone.length) return "";
        const lines = gone.map((d) => {
            const who = d?.name ? `${d.name}'s` : "A member's";
            const when = d?.deletedAt ? ` on ${moment(d.deletedAt).format("DD MMM YYYY, hh:mm A")}` : "";
            return `${who} account was deleted${when}.`;
        });
        return `${lines.join(" ")} Their messages are read from the chat record, so the whole conversation still shows.`;
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const getPaginationRange = () => {
        const range = [];
        for (let i = 1; i <= totalPages; i++) range.push(i);
        return range;
    };

    return (
        <div className="card">
            {/* HEADER */}
            <div className="card-header border-bottom d-flex  align-items-center flex-wrap gap-2">
                <select
                    className="form-select form-select-sm w-auto ps-12 py-6"
                    style={{
                        fontSize: "16px",
                        borderRadius: "7px",
                        backgroundPositionX: "unset",
                        backgroundPosition: "right 0.75rem center",
                    }}
                    value={limit}
                    onChange={(e) => {
                        setLimit(parseInt(e.target.value, 10));
                        setPage(1);
                    }}
                >
                    <option value={0}>All messages</option>
                    <option value={10}>10 items per page</option>
                    <option value={20}>20 items per page</option>
                    <option value={50}>50 items per page</option>
                    <option value={100}>100 items per page</option>
                </select>

                <form
                    className="navbar-search"
                    style={{ position: "relative" }}
                    onSubmit={(e) => e.preventDefault()}
                >
                    <input
                        type="text"
                        className="bg-base h-40-px w-auto pe-40"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        style={{
                            paddingRight: search ? "5rem" : "2.5rem",
                            borderRadius: "7px",
                        }}
                    />

                    {/* Search icon */}
                    <Icon
                        icon="ion:search-outline"
                        className="icon"
                        style={{
                            position: "absolute",
                            right: search ? "3.25rem" : "1rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: "1.25rem",
                            color: "#999",
                            pointerEvents: "none",
                        }}
                    />

                    {/* Clear button */}
                    {search && (
                        <button
                            type="button"
                            onClick={() => {
                                setSearch("");
                                setPage(1);
                            }}
                            style={{
                                position: "absolute",
                                right: "1rem",
                                top: "30%",
                                transform: "translateY(-50%)",
                                fontSize: "1.25rem",
                                color: "#000",
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                            }}
                        >
                            <Icon icon="ic:round-close" />
                        </button>
                    )}
                </form>
            </div>

            {/* NOTES: a member left, or the conversation is longer than one read */}
            {!loading && !loadError && (departedNote() || info.truncated) && (
                <div className="px-24 pt-16">
                    {departedNote() && (
                        <div className="bg-warning-100 text-warning-700 radius-4 px-16 py-8 text-sm mb-8">
                            {departedNote()}
                        </div>
                    )}
                    {info.truncated && (
                        <div className="bg-warning-100 text-warning-700 radius-4 px-16 py-8 text-sm mb-8">
                            This conversation is longer than the viewer reads at once. Only its first{" "}
                            {rows.length} messages are shown.
                        </div>
                    )}
                </div>
            )}

            {/* CHAT BODY */}
            <div
                className="card-body chat-box"
                ref={chatBoxRef}
                style={{ maxHeight: "500px", overflowY: "auto" }}
            >
                {!hasPair ? (
                    <div className="text-center py-4">No conversation selected.</div>
                ) : loading ? (
                    <div className="text-center py-4">Loading...</div>
                ) : loadError ? (
                    <div className="text-center py-4">
                        <div className="text-danger-600 fw-semibold mb-1">
                            This conversation could not be loaded.
                        </div>
                        <div className="text-secondary-light text-sm mb-3">{loadError}</div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={loadMessages}
                        >
                            Try again
                        </button>
                    </div>
                ) : pageRows.length > 0 ? (
                    pageRows.map((msg) => {
                        const isMine = String(msg.sender) === String(uid);
                        const senderName = getSenderName(msg);
                        const messageText = getMessageText(msg);
                        const messageClass = getMessageClass(msg, isMine);

                        return (
                            <div
                                key={msg.id}
                                className={`chat-message ${isMine
                                    ? "chat-message-right"
                                    : "chat-message-left"
                                    } mb-4`}
                            >
                                <div className="chat-avatar">
                                    <Icon icon="mdi:account-circle" width="34" />
                                </div>

                                <div className="chat-content">
                                    <div className="chat-sender fw-semibold mb-1">
                                        {senderName}
                                    </div>


                                    <div className={`chat-text ${messageClass}`}>
                                        {renderMessageContent(msg)}
                                    </div>

                                    <div className="chat-time text-muted small mt-1">

                                        {msg?.sentAt
                                            ? moment.unix(msg.sentAt).format("DD MMM YYYY, hh:mm A")
                                            : "-"}
                                    </div>


                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center py-4">
                        {needle
                            ? "No messages contain this text."
                            : "No messages in this conversation."}
                    </div>
                )}
            </div>
            {/* PAGINATION */}
            {!loadError && total > 0 && (
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 p-3 border-top">
                    <span>
                        {limit > 0
                            ? `Showing ${(page - 1) * limit + 1} to ${Math.min(page * limit, total)} of ${total} ${total === 1 ? "message" : "messages"}`
                            : total === 1
                                ? "Showing 1 message"
                                : `Showing all ${total} messages`}
                    </span>

                    {limit > 0 && totalPages > 1 && (
                    <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center mb-0">
                        {/* PREV */}
                        <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                            <button
                                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                            >
                                <Icon icon="ep:d-arrow-left" />
                            </button>
                        </li>

                        {/* PAGES */}
                        {getPaginationRange().map((p) => (
                            <li key={p} className="page-item">
                                <button
                                    className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${page === p
                                        ? "bg-primary-600 text-white"
                                        : "bg-neutral-200 text-secondary-light"
                                        }`}
                                    onClick={() => handlePageChange(p)}
                                >
                                    {p}
                                </button>
                            </li>
                        ))}

                        {/* NEXT */}
                        <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                            <button
                                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === totalPages}
                            >
                                <Icon icon="ep:d-arrow-right" />
                            </button>
                        </li>
                    </ul>
                    )}
                </div>
            )}

        </div>
    );
};

export default ChatMessagesTable;